import React, { useState, useRef, useEffect } from "react";

import useMobileDetect from "use-mobile-detect-hook";

import maplibregl from "!maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";

import LayerToggle from "./maps/LayerToggle";
import RasterCoverageSelector from "./maps/RasterCoverageSelector";
import Spinner from "./Spinner";

export const MapContext = React.createContext(null);

export default function Map({
  allowExpand,
  bounds,
  center,
  children,
  fitBoundsOptions,
  loader,
  minZoom,
  maxZoom,
  zoom,
  coverages,
  showLayerToggle,
}) {
  const mapContainer = useRef(null);
  const map = useRef(null);

  const detectMobile = useMobileDetect();

  const [supported, setSupported] = useState(true);
  const [loading, setLoading] = useState(true);

  const [satelliteActive, setSatelliteActive] = useState(null);
  const handleSatelliteClick = () => {
    setSatelliteActive(!satelliteActive);
  };

  const [activeCoverage, setActiveCoverage] = useState(null);
  const handleSatelliteSelect = (coverage) => {
    setActiveCoverage(coverage);
  };

  function layerMatches(layer, id, source) {
    if (typeof id !== "undefined") {
      if (id instanceof RegExp) {
        if (layer.id.match(id)) {
          return true;
        }
      } else {
        if (layer.id === id) {
          return true;
        }
      }
    }

    if (typeof source !== "undefined") {
      if (source instanceof RegExp) {
        if (layer.source.match(source)) {
          return true;
        }
      } else {
        if (layer.source === source) {
          return true;
        }
      }
    }
  }

  function hideLayers({ id, source } = {}) {
    if (!map.current) return;

    map.current.getStyle().layers.forEach((layer) => {
      if (layerMatches(layer, id, source)) {
        map.current.setLayoutProperty(layer.id, "visibility", "none");
      }
    });
  }

  function showLayers({ id, source } = {}) {
    if (!map.current) return;

    map.current.getStyle().layers.forEach((layer) => {
      if (layerMatches(layer, id, source)) {
        map.current.setLayoutProperty(layer.id, "visibility", "visible");
      }
    });
  }

  useEffect(() => {
    if (map.current) return; // initialize map only once

    let options = {
      container: mapContainer.current,
      style: "/map/style.json",
      trackResize: detectMobile.isMobile() ? false : true,
    };

    if (bounds) {
      options.bounds = bounds;
    }

    if (fitBoundsOptions) {
      options.fitBoundsOptions = fitBoundsOptions;
    }

    if (center) {
      options.center = center;
    }

    if (minZoom) {
      options.minZoom = minZoom;
    }

    if (maxZoom) {
      options.maxZoom = maxZoom;
    }

    if (zoom) {
      options.zoom = zoom;
    }

    if (!maplibregl.supported()) {
      setSupported(false);
      _vaTrack("map.unsupported");
      return;
    }

    map.current = new maplibregl.Map(options);
    map.current.setLoading = setLoading;

    map.current.on("idle", (e) => {
      setLoading(false);
    });

    document.addEventListener("webkitfullscreenchange", () =>
      setTimeout(() => map.current.resize(), 0)
    );

    // Add raster coverages, if any
    map.current.on("load", () => {
      if (coverages && coverages.length > 0) {
        coverages.forEach((coverage) => {
          map.current.addSource(`coverage-${coverage.id}`, {
            type: "raster",
            scheme: "tms",
            tiles: [coverage.tilesUrl],
            minzoom: coverage.minZoom,
            maxZoom: coverage.maxZoom,
            tileSize: 256,
          });

          map.current.addLayer(
            {
              id: `coverage-layer-${coverage.id}`,
              type: "raster",
              source: `coverage-${coverage.id}`,
              layout: {
                visibility: "none",
              },
            },
            "satellite-tunnel"
          );
        });
      }
    });

    if (!detectMobile.isMobile()) {
      if (allowExpand) {
        map.current.addControl(new maplibregl.FullscreenControl(), "top-left");
      }

      map.current.addControl(
        new maplibregl.NavigationControl({ showCompass: false }),
        "top-left"
      );
    }

    // disable map rotation using right click + drag
    map.current.dragRotate.disable();

    // disable map rotation using touch rotation gesture
    map.current.touchZoomRotate.disableRotation();
  });

  useEffect(() => {
    if (!map.current) return;
    if (satelliteActive === null) return;

    if (satelliteActive) {
      showLayers({ id: /satellite-/ });
    } else {
      setActiveCoverage(null);
      hideLayers({ id: /satellite-/ });
      hideLayers({ id: /coverage-layer-/ });
    }
  }, [satelliteActive]);

  useEffect(() => {
    if (!map.current) return;
    if (satelliteActive === null) return;

    if (activeCoverage) {
      hideLayers({ id: /coverage-layer-/ });
      showLayers({ id: `coverage-layer-${activeCoverage}` });
    } else {
      hideLayers({ id: /coverage-layer-/ });
    }
  }, [activeCoverage]);

  useEffect(() => {
    window.addEventListener("center-map", (e) => {
      map.current.setCenter([e.detail.lng, e.detail.lat]);
      map.current.zoomTo(16);
      setSatelliteActive(true);
    });

    return () => {
      window.removeEventListener("center-map");
    };
  }, [map]);

  if (!supported) {
    return (
      <div className="bg-neutral-100 text-sm text-neutral-500 text-center p-8 h-full w-full flex items-center">
        Sorry, our maps are not supported by your browser.
      </div>
    );
  }

  return (
    <MapContext.Provider value={map.current}>
      <div ref={mapContainer} className="w-full h-full">
        {showLayerToggle && (
          <div className="maplibregl-ctrl-group absolute z-50 top-[10px] right-[10px] px-2 py-1 bg-white space-x-3 flex flex-row items-center">
            {coverages && coverages.length > 0 ? (
              <RasterCoverageSelector
                coverages={coverages}
                checked={satelliteActive}
                onChange={handleSatelliteClick}
                onSelect={handleSatelliteSelect}
              />
            ) : (
              <LayerToggle
                checked={satelliteActive}
                onChange={handleSatelliteClick}
              >
                Satellite
              </LayerToggle>
            )}
          </div>
        )}
        {loader != false && loading && (
          <div className="absolute inset-0 z-50 grid place-items-center bg-white/25">
            <div className="w-24 h-24">
              <Spinner />
            </div>
          </div>
        )}
        {children}
      </div>
    </MapContext.Provider>
  );
}

Map.defaultProps = {
  showLayerToggle: true,
};
